import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, Link as LinkIcon, Loader2, File as FileIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export function FileUpload({ onFileSelected, currentFile, folderPath }) {
  const [uploadMethod, setUploadMethod] = useState('upload');
  const [fileUrl, setFileUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(
    currentFile ? {
      url: currentFile.url || currentFile,
      name: currentFile.name || 'File',
      size: currentFile.size || null
    } : null
  );

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }

    setIsUploading(true);

    try {
      // Generate a unique filename
      const timestamp = Date.now();
      const originalName = file.name;
      const fileExtension = originalName.split('.').pop();
      const fileName = `${timestamp}-${Math.random().toString(36).substring(7)}.${fileExtension}`;

      // Get signed upload URL from edge function
      const { data: urlData, error: urlError } = await supabase.functions.invoke('generate-upload-url', {
        body: {
          action: 'upload',
          fileName: fileName,
          contentType: file.type || 'application/octet-stream',
          folderPath: folderPath,
        },
      });

      if (urlError) throw urlError;

      // Upload file to GCS using signed URL
      const uploadResponse = await fetch(urlData.signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      // Set the uploaded file data
      const fileData = {
        url: urlData.publicUrl,
        name: originalName,
        size: file.size
      };

      setUploadedFile(fileData);
      onFileSelected(fileData);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (!fileUrl) {
      toast.error('Please enter a file URL');
      return;
    }

    const trimmed = fileUrl.trim();
    const isVariable = /\{\{.*?\}\}/.test(trimmed);

    if (!isVariable) {
      // Basic URL validation for non-variable URLs
      try {
        new URL(trimmed);
      } catch {
        toast.error('Please enter a valid URL or {{ variable }}');
        return;
      }
    }

    // Extract filename from URL, or use variable as name
    const urlParts = trimmed.split('/');
    const fileName = isVariable ? trimmed : (urlParts[urlParts.length - 1] || 'File');

    const fileData = {
      url: trimmed,
      name: fileName,
      size: null
    };

    setUploadedFile(fileData);
    onFileSelected(fileData);
    toast.success(isVariable ? 'Variable URL added' : 'File URL added');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      <Tabs value={uploadMethod} onValueChange={setUploadMethod}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload">Upload</TabsTrigger>
          <TabsTrigger value="url">File URL</TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-muted-foreground/50 transition-colors">
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">Upload File</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Choose a file from your computer
            </p>
            <label htmlFor="file-upload">
              <Button asChild disabled={isUploading}>
                <span>
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Choose File'
                  )}
                </span>
              </Button>
              <input
                id="file-upload"
                type="file"
                accept="*/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          </div>
          <div className="text-xs text-muted-foreground text-center">
            Any file type supported (Max 50MB)
          </div>
        </TabsContent>

        {/* URL Tab */}
        <TabsContent value="url" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="file-url">File URL</Label>
            <div className="flex gap-2">
              <Input
                id="file-url"
                placeholder="https://example.com/file.pdf or {{ variable }}"
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUrlSubmit();
                  }
                }}
              />
              <Button onClick={handleUrlSubmit}>
                <LinkIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* File Info Display */}
      {uploadedFile && (
        <div className="space-y-2">
          <Label>Uploaded File</Label>
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center gap-3">
              <FileIcon className="h-8 w-8 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{uploadedFile.name}</div>
                {uploadedFile.size && (
                  <div className="text-xs text-muted-foreground">
                    {formatFileSize(uploadedFile.size)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
